/**
 * Honeybadger Error Tracking Configuration
 * @memberof @albert-io/notifier
 */

import React from 'react';
import {Honeybadger, HoneybadgerErrorBoundary} from '@honeybadger-io/react';

import {ERROR_TYPES} from 'client/constants';
import ErrorPage from 'client/static/Error/ErrorPage.react';
// The session leaf module, NOT '@albert-io/mandark/authentication' or SessionStore:
// both of those import this module back (authentication -> notifier for decode()
// reporting), which is a module cycle.
import {getSession} from '@albert-io/mandark/authentication/session';
import {shouldReportError, isCurrentUrlMatch} from './filters';
import {getHoneybadgerEnvironment, isKnownReportingHost} from './environment';
import {shouldSuppressStaleBundleNotice} from './staleBundle';

// configuration

const CONTENT_FILTERS = [
  'token',
  'email',
  'username',
  'password',
  'verify_old_password',
  'password_reset_link',
  'verify_password_reset_link',
  'stripe_customer_id',
  'first_name',
  'last_name',
  'salutation',
  'consent_email'
];

const getReleaseVersion = () => {
  const path = process.env.STATIC_ASSETS_APP_PATH || '';
  if (!path) return '';

  const segments = path.split('/').filter((segment) => segment.length > 0);
  return segments.length > 0 ? segments[segments.length - 1] : '';
};

const RELEASE_VERSION = getReleaseVersion();

// Normalize custom tags to array format as they can be string or array,
function normalizeCustomTags(customTags) {
  if (!customTags) return [];
  if (Array.isArray(customTags)) return customTags.filter(Boolean);
  return [customTags];
}

// Build environment tags as an array for Honeybadger
// Honeybadger now accepts tags as an array instead of comma-separated string

const buildEnvironmentTags = (customTags, userAgent) => {
  const isIpad = /iPad/.test(userAgent);
  const isDennis = isCurrentUrlMatch('/dennis');

  const customTagsArray = normalizeCustomTags(customTags);

  // Build complete tags array
  const tags = [
    RELEASE_VERSION,
    getSession().isValid() ? getSession().getAccountType() : 'unauthenticated',
    ...(isIpad ? ['ipad'] : []),
    ...(isDennis ? ['dennis'] : []),
    ...customTagsArray
  ];

  // Remove duplicates and empty values
  return [...new Set(tags.filter(Boolean))];
};

const buildErrorContext = (existingContext) => {
  const hasTranslate = global.document.getElementsByClassName('translated-ltr').length > 0;

  return {
    ...existingContext,
    user_id: getSession().isValid() ? getSession().getId() : null,
    hasTranslate,
    route: {pathname: window.location.pathname}
  };
};

const initializeHoneybadger = () => {
  Honeybadger.configure({
    apiKey: process.env.HONEYBADGER_API_KEY,
    environment: getHoneybadgerEnvironment(),
    filters: CONTENT_FILTERS,
    projectRoot: 'webpack:///./',
    reportData: true,
    revision: RELEASE_VERSION,
    maxErrors: 20,
    breadcrumbsEnabled: {
      dom: true,
      network: true,
      navigation: true,
      console: false
    },
    maxBreadcrumbs: 50
  });

  Honeybadger.beforeNotify((notice) => {
    const userAgent = navigator.userAgent || '';
    notice.tags = buildEnvironmentTags(notice.tags, userAgent);
    notice.context = buildErrorContext(notice.context);
    return shouldReportError(notice) && !shouldSuppressStaleBundleNotice(RELEASE_VERSION);
  });
};

// One flag gates BOTH init and notify() so they can't drift. The host gate
// matters because the key is baked into every bundle — without it, localhost
// and rehosted copies (translation proxies, mirrors) would report too.
const isReportingEnabled = Boolean(process.env.HONEYBADGER_API_KEY) && isKnownReportingHost();

if (isReportingEnabled) {
  initializeHoneybadger();
} else if (process.env.HONEYBADGER_API_KEY) {
  // Fails closed — a keyed build on an unlisted host reports NOTHING, so warn
  // loudly to make the missing ENVIRONMENT_RULES entry discoverable.
  console.warn(
    `Honeybadger reporting is disabled for host "${global.window?.location?.hostname}". ` +
      'If this is a legitimate serving host, add it to ENVIRONMENT_RULES in ' +
      '@albert-io/notifier services/honeybadger/environment.js.'
  );
}

/**
 * Report errors to Honeybadger - lightweight wrapper around Honeybadger.notify
 *
 * @example
 * notifier.notify(error, {
 *   message: 'Failed to save',
 *   tags: 'ComponentName',
 *   fingerprint: 'unique-error-id'
 * })
 */
const notifier = {
  notify(error, options) {
    if (!isReportingEnabled) {
      console.warn('Honeybadger reporting is disabled; skipping error report.', error);
      return;
    }

    Honeybadger.notify(error, options);
  }
};

export const ErrorBoundary = ({children}) => (
  <HoneybadgerErrorBoundary
    honeybadger={Honeybadger}
    ErrorComponent={() => <ErrorPage errorCode={ERROR_TYPES.GENERIC} />}
  >
    {children}
  </HoneybadgerErrorBoundary>
);

export default notifier;
